import React from 'react';

/**
 * Shared GlowRing component for the Industrial Pro theme.
 * Renders a neon glowing circular progress ring.
 *
 * Props:
 *  value       - current numeric value
 *  max         - maximum value (default 100)
 *  color       - 'lime' | 'orange' | 'cyan' | 'purple' (default 'cyan')
 *  label       - optional label below the ring
 *  sublabel    - optional small text inside ring below value
 *  size        - diameter in px (default 160)
 *  strokeWidth - stroke width (default 10)
 *  displayValue - override the center display text
 *  unit        - unit text shown below value inside ring
 *  showPercent - show percentage below ring (default false)
 */
const GlowRing = ({
  value,
  max = 100,
  color = 'cyan',
  label,
  sublabel,
  size = 160,
  strokeWidth = 10,
  displayValue,
  unit,
  showPercent = false,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth * 2 - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  const colorMap = {
    lime: {
      stroke: '#AAFF00',
      text: '#AAFF00',
      shadow: '0 0 20px #AAFF0066, 0 0 40px #AAFF0033',
      glow: 'rgba(170,255,0,0.5)',
    },
    orange: {
      stroke: '#FF6B00',
      text: '#FF6B00',
      shadow: '0 0 20px #FF6B0066, 0 0 40px #FF6B0033',
      glow: 'rgba(255,107,0,0.5)',
    },
    cyan: {
      stroke: '#00D4FF',
      text: '#00D4FF',
      shadow: '0 0 20px #00D4FF66, 0 0 40px #00D4FF33',
      glow: 'rgba(0,212,255,0.5)',
    },
    purple: {
      stroke: '#A855F7',
      text: '#A855F7',
      shadow: '0 0 20px rgba(168,85,247,0.5), 0 0 40px rgba(168,85,247,0.25)',
      glow: 'rgba(168,85,247,0.5)',
    },
  };

  const c = colorMap?.[color] || colorMap?.cyan;
  const filterId = `glow-ring-${color}-${Math.random()?.toString(36)?.substr(2, 5)}`;
  const centerText = displayValue !== undefined ? displayValue : (typeof value === 'number' ? value?.toLocaleString() : value);
  const fontSize = size > 140 ? '1.75rem' : size > 100 ? '1.25rem' : '1rem';

  return (
    <div className="flex flex-col items-center gap-2">
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <defs>
            <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={c?.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            filter={`url(#${filterId})`}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        {/* Center text */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontFamily: "'Inter Tight', 'Inter', sans-serif",
              fontWeight: 800,
              fontSize,
              color: c?.text,
              textShadow: c?.shadow,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {centerText}
          </span>
          {(sublabel || unit) && (
            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>
              {sublabel || unit}
            </span>
          )}
        </div>
      </div>
      {(label || showPercent) && (
        <div className="text-center">
          {label && (
            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: c?.text, fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
              {label}
            </p>
          )}
          {showPercent && (
            <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {percentage?.toFixed(0)}%
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GlowRing;
